import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiProductosInstance, authHeaderProductos } from "../../redux/actions/productos/productos";

import {
  Form,
  Input,
  Select,
  Button,
  Row,
  Col,
  Typography,
  Space,
  InputNumber,
  Modal,
  Dropdown,
} from "antd";
import { toast } from "../../components/toasts/toastStore";
import { CheckOutlined, MoreOutlined } from "@ant-design/icons";

import "./EventosPage.css";
import "./PaquetesPage.css";

const { Title, Text } = Typography;

const EMPTY_VARIANTE = () => ({
  key: Date.now() + Math.random(),
  id_variante: null,
  clave: "",
  descripcion_variante: "",
  id_unidad_medida: null,
  precio_venta: "",
  costo_promedio: "",
  costo_conocido: false,
  existencia: "",
  stock_minimo: "",
  confirmed: false,
});

const fmt = (n) =>
  n == null || n === "" ? "—" : `$${Number(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function CrearProductoPage() {
  const navigate = useNavigate();
  const { idProducto } = useParams();
  const isEditing = !!idProducto;

  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [variantes, setVariantes] = useState([EMPTY_VARIANTE()]);

  const [categorias, setCategorias] = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);
  const [unidades, setUnidades] = useState([]);

  const [crearCategoriaOpen, setCrearCategoriaOpen] = useState(false);
  const [nuevaCategoriaNombre, setNuevaCategoriaNombre] = useState("");
  const [creandoCategoria, setCreandoCategoria] = useState(false);

  const [crearUnidadOpen, setCrearUnidadOpen] = useState(false);
  const [nuevaUnidadNombre, setNuevaUnidadNombre] = useState("");
  const [creandoUnidad, setCreandoUnidad] = useState(false);

  const idCategoriaSeleccionada = Form.useWatch("id_categoria", form);

  const cargarCatalogos = () => {
    const headers = authHeaderProductos();
    apiProductosInstance.get("/categorias", { headers })
      .then((r) => setCategorias((r.data?.items ?? []).map((c) => ({ label: c.nombre, value: c.id_categoria }))))
      .catch(() => {});
    apiProductosInstance.get("/unidades-medida", { headers })
      .then((r) => setUnidades((r.data?.items ?? []).map((u) => ({ label: u.nombre, value: u.id_unidad_medida }))))
      .catch(() => {});
  };

  useEffect(() => { cargarCatalogos(); }, []);

  useEffect(() => {
    if (!idCategoriaSeleccionada) {
      setSubcategorias([]);
      return;
    }
    apiProductosInstance
      .get("/subcategorias", { headers: authHeaderProductos(), params: { id_categoria: idCategoriaSeleccionada } })
      .then((r) => setSubcategorias((r.data?.items ?? []).map((s) => ({ label: s.nombre, value: s.id_subcategoria }))))
      .catch(() => {});
  }, [idCategoriaSeleccionada]);

  useEffect(() => {
    if (!isEditing) return;
    const headers = authHeaderProductos();
    apiProductosInstance.get(`/productos/${idProducto}`, { headers })
      .then((res) => {
        const p = res.data ?? {};
        form.setFieldsValue({
          nombre: p.nombre,
          id_categoria: p.id_categoria,
          id_subcategoria: p.id_subcategoria,
          artista: p.artista,
          descripcion: p.descripcion,
          tasa_iva: p.tasa_iva != null ? parseFloat(p.tasa_iva) : undefined,
        });
        const filas = (p.variantes ?? []).map((v) => ({
          key: v.id_variante,
          id_variante: v.id_variante,
          clave: v.clave || "",
          descripcion_variante: v.descripcion_variante || "",
          id_unidad_medida: v.id_unidad_medida,
          precio_venta: v.precio_venta != null ? String(v.precio_venta) : "",
          costo_promedio: v.costo_promedio != null ? String(v.costo_promedio) : "",
          costo_conocido: Boolean(v.costo_conocido),
          existencia: v.existencia != null ? String(v.existencia) : "",
          stock_minimo: v.stock_minimo != null ? String(v.stock_minimo) : "",
          confirmed: true,
        }));
        setVariantes(filas.length > 0 ? [...filas, EMPTY_VARIANTE()] : [EMPTY_VARIANTE()]);
      })
      .catch(() => toast.error("No se pudo cargar el producto"));
  }, [idProducto]);

  const updateVariante = (index, field, value) => {
    setVariantes((prev) => prev.map((v, i) => (i === index ? { ...v, [field]: value } : v)));
  };

  const confirmVariante = (index) => {
    const v = variantes[index];
    if (!v.clave.trim()) return toast.warning("Ingresa la clave (SKU) de la variante");
    if (!v.id_unidad_medida) return toast.warning("Selecciona la unidad de medida");
    if (v.precio_venta === "" || v.precio_venta == null) return toast.warning("Ingresa el precio de venta");

    setVariantes((prev) => {
      const updated = prev.map((item, i) => (i === index ? { ...item, confirmed: true } : item));
      const hasOpenRow = updated.some((item) => !item.confirmed);
      return hasOpenRow ? updated : [...updated, EMPTY_VARIANTE()];
    });
  };

  const editVariante = (index) => {
    setVariantes((prev) => prev.map((v, i) => (i === index ? { ...v, confirmed: false } : v)));
  };

  const removeVariante = (index) => {
    setVariantes((prev) => {
      const next = prev.filter((_, i) => i !== index);
      const hasOpenRow = next.some((v) => !v.confirmed);
      return hasOpenRow || next.length === 0 ? next : [...next, EMPTY_VARIANTE()];
    });
  };

  const handleCrearCategoria = async () => {
    if (!nuevaCategoriaNombre.trim()) return toast.warning("Escribe un nombre");
    setCreandoCategoria(true);
    try {
      const { data } = await apiProductosInstance.post(
        "/categorias",
        { nombre: nuevaCategoriaNombre.trim() },
        { headers: authHeaderProductos() }
      );
      const nuevo = { label: data.nombre, value: data.id_categoria };
      setCategorias((prev) => [...prev, nuevo]);
      form.setFieldValue("id_categoria", nuevo.value);
      setCrearCategoriaOpen(false);
      setNuevaCategoriaNombre("");
      toast.success("Categoria creada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear la categoria");
    } finally {
      setCreandoCategoria(false);
    }
  };

  const handleCrearUnidad = async () => {
    if (!nuevaUnidadNombre.trim()) return toast.warning("Escribe un nombre");
    setCreandoUnidad(true);
    try {
      const { data } = await apiProductosInstance.post(
        "/unidades-medida",
        { nombre: nuevaUnidadNombre.trim() },
        { headers: authHeaderProductos() }
      );
      const nuevo = { label: data.nombre, value: data.id_unidad_medida };
      setUnidades((prev) => [...prev, nuevo]);
      setCrearUnidadOpen(false);
      setNuevaUnidadNombre("");
      toast.success("Unidad creada");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear la unidad");
    } finally {
      setCreandoUnidad(false);
    }
  };

  const handleSave = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const validVariantes = variantes
      .filter((v) => v.confirmed && v.clave)
      .map((v) => ({
        id_variante: v.id_variante || undefined,
        clave: v.clave.trim(),
        descripcion_variante: v.descripcion_variante?.trim() || null,
        id_unidad_medida: v.id_unidad_medida,
        precio_venta: parseFloat(v.precio_venta) || 0,
        costo_promedio: v.costo_promedio !== "" ? parseFloat(v.costo_promedio) : null,
        costo_conocido: Boolean(v.costo_conocido),
        existencia: v.existencia !== "" ? parseFloat(v.existencia) : 0,
        stock_minimo: v.stock_minimo !== "" ? parseFloat(v.stock_minimo) : null,
      }));

    if (validVariantes.length === 0) {
      toast.error("Agrega al menos una variante y confirmala");
      return;
    }

    const payload = {
      nombre: values.nombre?.trim(),
      id_categoria: values.id_categoria,
      id_subcategoria: values.id_subcategoria || null,
      artista: values.artista?.trim() || null,
      descripcion: values.descripcion?.trim() || null,
      tasa_iva: values.tasa_iva != null ? values.tasa_iva : null,
      variantes: validVariantes,
    };

    setSaving(true);
    try {
      if (isEditing) {
        await apiProductosInstance.patch(`/productos/${idProducto}`, payload, { headers: authHeaderProductos() });
        toast.success("Producto actualizado correctamente");
      } else {
        await apiProductosInstance.post("/productos", payload, { headers: authHeaderProductos() });
        toast.success("Producto creado exitosamente");
      }
      navigate("/app/productos");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="paq-main">
      <div className="paq-content">
        <section className="cc-page-header-card">
          <Space direction="vertical" size={2}>
            <Title level={2} className="eventos-title" style={{ marginBottom: 0 }}>
              {isEditing ? "Editando producto" : "Nuevo producto"}
            </Title>
            <Text className="eventos-subtitle">
              {isEditing
                ? "Modifica los datos del producto y guarda los cambios."
                : "Completa los datos del producto para registrarlo en el sistema."}
            </Text>
          </Space>

          <Space>
            <Button className="eventos-btn-clean" onClick={() => navigate("/app/productos")} disabled={saving}>
              Cancelar
            </Button>
            <Button
              type="primary"
              loading={saving}
              icon={!isEditing ? <i className="fa-solid fa-plus" /> : null}
              onClick={handleSave}
              style={{ backgroundColor: "#f90336", borderColor: "#f90336" }}
            >
              {isEditing ? "Guardar cambios" : "Crear producto"}
            </Button>
          </Space>
        </section>

        <Form form={form} layout="vertical">
          <div className="cc-section-card">
            <div className="cc-section-header">
              <span className="cc-section-icon"><i className="fa-solid fa-compact-disc"></i></span>
              Datos del producto
            </div>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="nombre"
                  label={<span className="paq-field-label">Nombre del producto</span>}
                  rules={[{ required: true, message: "Requerido" }]}
                >
                  <Input placeholder="Ej. Parachutes (Vinyl)" autoComplete="off" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="artista" label={<span className="paq-field-label">Artista (opcional)</span>}>
                  <Input placeholder="Ej. Coldplay" autoComplete="off" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  name="id_categoria"
                  label={<span className="paq-field-label">Categoria</span>}
                  rules={[{ required: true, message: "Requerido" }]}
                >
                  <Select placeholder="Selecciona categoria" options={categorias} showSearch optionFilterProp="label" />
                </Form.Item>
                <div className="cc-select-helper">
                  No encuentras la categoria que buscas?{" "}
                  <a className="cc-select-helper-link" onClick={() => setCrearCategoriaOpen(true)}>Crea una nueva</a>
                </div>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item name="id_subcategoria" label={<span className="paq-field-label">Subcategoria (opcional)</span>}>
                  <Select
                    placeholder={idCategoriaSeleccionada ? "Selecciona subcategoria" : "Selecciona una categoria primero"}
                    options={subcategorias}
                    disabled={!idCategoriaSeleccionada}
                    showSearch
                    optionFilterProp="label"
                    allowClear
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item name="tasa_iva" label={<span className="paq-field-label">Tasa de IVA % (opcional)</span>}>
                  <InputNumber style={{ width: "100%" }} min={0} max={100} precision={2} placeholder="16.00" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item name="descripcion" label={<span className="paq-field-label">Descripcion</span>}>
                  <Input.TextArea rows={3} placeholder="Descripcion breve del producto..." autoComplete="off" />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <div className="cc-section-card" style={{ marginTop: 20, paddingBottom: 20 }}>
            <div className="cc-section-header">
              <span className="cc-section-icon"><i className="fa-solid fa-list" /></span>
              Variantes (SKU, precio y existencia)
            </div>

            <Text style={{ fontSize: 12, color: "#64748b", display: "block", marginBottom: 14 }}>
              Cada producto necesita al menos una variante para poder venderse.
            </Text>

            <div style={{ overflowX: "auto" }}>
              <div style={{ minWidth: 1180 }}>
                <div style={{ display: "flex", gap: 8, marginBottom: 6, padding: "0 8px" }}>
                  <div style={{ width: 140, fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Clave (SKU)</div>
                  <div style={{ flex: 1, minWidth: 160, fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Descripcion</div>
                  <div style={{ width: 140, fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Unidad</div>
                  <div style={{ width: 130, fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Precio venta</div>
                  <div style={{ width: 120, fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Costo</div>
                  <div style={{ width: 110, fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Existencia</div>
                  <div style={{ width: 120, fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>Stock minimo</div>
                  <div style={{ width: 130 }} />
                </div>

                {variantes.map((v, index) =>
                  v.confirmed ? (
                    <div key={v.key} style={{ display: "flex", gap: 8, alignItems: "center", padding: "10px 12px", marginBottom: 6, background: "#f8fafc", borderRadius: 8, border: "1px solid #e5e7eb" }}>
                      <div style={{ width: 140, fontSize: 13, fontWeight: 600, color: "#1f2937" }}>{v.clave}</div>
                      <div style={{ flex: 1, minWidth: 160, fontSize: 13, color: "#374151" }}>{v.descripcion_variante || "—"}</div>
                      <div style={{ width: 140, fontSize: 13, color: "#64748b" }}>{unidades.find((u) => u.value === v.id_unidad_medida)?.label || "—"}</div>
                      <div style={{ width: 130, fontSize: 13, fontWeight: 600, color: "#1f2937" }}>{fmt(v.precio_venta)}</div>
                      <div style={{ width: 120, fontSize: 13, color: "#374151" }}>{fmt(v.costo_promedio)}</div>
                      <div style={{ width: 110, fontSize: 13, color: "#374151" }}>{v.existencia || "0"}</div>
                      <div style={{ width: 120, fontSize: 13, color: "#374151" }}>{v.stock_minimo || "—"}</div>
                      <div style={{ width: 130, display: "flex", justifyContent: "flex-end" }}>
                        <Dropdown
                          menu={{
                            items: [
                              { key: "edit", label: "Editar", icon: <i className="fa-solid fa-pen" /> },
                              { key: "delete", label: "Eliminar", icon: <i className="fa-solid fa-trash" />, danger: true },
                            ],
                            onClick: ({ key }) => (key === "edit" ? editVariante(index) : removeVariante(index)),
                          }}
                          trigger={["click"]}
                          placement="bottomRight"
                        >
                          <button type="button" style={{ width: 32, height: 32, border: "1px solid #e5e7eb", background: "#fff", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <MoreOutlined style={{ fontSize: 16, color: "#6b7280" }} />
                          </button>
                        </Dropdown>
                      </div>
                    </div>
                  ) : (
                    <div key={v.key} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, padding: "8px", background: "#fffdf5", borderRadius: 8, border: "1px dashed #d1d5db" }}>
                      <Input style={{ width: 140 }} placeholder="SKU" value={v.clave} onChange={(e) => updateVariante(index, "clave", e.target.value)} />
                      <Input style={{ flex: 1, minWidth: 160 }} placeholder="Descripcion (opcional)" value={v.descripcion_variante} onChange={(e) => updateVariante(index, "descripcion_variante", e.target.value)} />
                      <Select
                        style={{ width: 140 }}
                        placeholder="Unidad"
                        options={unidades}
                        value={v.id_unidad_medida || undefined}
                        onChange={(val) => updateVariante(index, "id_unidad_medida", val)}
                        showSearch
                        optionFilterProp="label"
                        dropdownRender={(menu) => (
                          <>
                            {menu}
                            <div style={{ padding: 6, borderTop: "1px solid #f0f0f0" }}>
                              <a onClick={() => setCrearUnidadOpen(true)} style={{ fontSize: 12, color: "#f90336" }}>+ Nueva unidad</a>
                            </div>
                          </>
                        )}
                      />
                      <InputNumber style={{ width: 130 }} placeholder="Precio" min={0} precision={2} value={v.precio_venta !== "" ? v.precio_venta : undefined} onChange={(val) => updateVariante(index, "precio_venta", val ?? "")} />
                      <InputNumber style={{ width: 120 }} placeholder="Costo" min={0} precision={2} value={v.costo_promedio !== "" ? v.costo_promedio : undefined} onChange={(val) => updateVariante(index, "costo_promedio", val ?? "")} />
                      <InputNumber style={{ width: 110 }} placeholder="Existencia" min={0} precision={3} value={v.existencia !== "" ? v.existencia : undefined} onChange={(val) => updateVariante(index, "existencia", val ?? "")} />
                      <InputNumber style={{ width: 120 }} placeholder="Stock min." min={0} precision={3} value={v.stock_minimo !== "" ? v.stock_minimo : undefined} onChange={(val) => updateVariante(index, "stock_minimo", val ?? "")} />
                      <div style={{ width: 130, display: "flex", gap: 6, justifyContent: "flex-end" }}>
                        <Button icon={<CheckOutlined />} onClick={() => confirmVariante(index)} className="eventos-btn-clean" style={{ width: 90 }}>
                          Aceptar
                        </Button>
                        <button type="button" className="paq-contenido-delete-btn" onClick={() => removeVariante(index)} title="Eliminar">
                          <i className="fa-solid fa-trash" />
                        </button>
                      </div>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </Form>
      </div>

      <Modal
        open={crearCategoriaOpen}
        onCancel={() => setCrearCategoriaOpen(false)}
        onOk={handleCrearCategoria}
        okText="Crear categoria"
        cancelText="Cancelar"
        title={<span><i className="fa-solid fa-tag" style={{ marginRight: 8, color: "#f90336" }} /> Nueva categoria</span>}
        confirmLoading={creandoCategoria}
        centered
        okButtonProps={{ style: { background: "#f90336", borderColor: "#f90336" } }}
      >
        <Input value={nuevaCategoriaNombre} onChange={(e) => setNuevaCategoriaNombre(e.target.value)} placeholder="Nombre de la categoria..." autoFocus />
      </Modal>

      <Modal
        open={crearUnidadOpen}
        onCancel={() => setCrearUnidadOpen(false)}
        onOk={handleCrearUnidad}
        okText="Crear unidad"
        cancelText="Cancelar"
        title={<span><i className="fa-solid fa-ruler" style={{ marginRight: 8, color: "#f90336" }} /> Nueva unidad de medida</span>}
        confirmLoading={creandoUnidad}
        centered
        okButtonProps={{ style: { background: "#f90336", borderColor: "#f90336" } }}
      >
        <Input value={nuevaUnidadNombre} onChange={(e) => setNuevaUnidadNombre(e.target.value)} placeholder="Ej. Pieza, Caja, Kilogramo..." autoFocus />
      </Modal>
    </main>
  );
}
